// Sistema de Logs Estruturados para Pagamentos
// Baseado na Suite de Testes Pagar.me

export interface PaymentLogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'debug';
  event: string;
  data: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
  reservationId?: string;
  transactionId?: string;
}

export interface PaymentMetrics {
  totalAttempts: number;
  successfulPayments: number;
  failedPayments: number;
  averageProcessingTime: number;
  errorsByType: Record<string, number>;
  testCardUsage: Record<string, number>;
}

export class PaymentLogger {
  private static logs: PaymentLogEntry[] = [];
  private static maxLogs = 1000; // Manter apenas os últimos 1000 logs
  
  /**
   * Determina se logs devem ser emitidos no console da UI.
   * Usa mesma regra do util `devLog`: habilita em DEV ou quando `VITE_ENABLE_UI_LOGS==='true'`.
   */
  private static isUiLogEnabled(): boolean {
    const viteDev = import.meta?.env?.DEV === true;
    const flag = String(import.meta?.env?.VITE_ENABLE_UI_LOGS ?? 'false').toLowerCase() === 'true';
    return viteDev || flag;
  }
  
  /**
   * Gera ID único para o log
   */
  private static generateId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Adiciona entrada de log
   */
  private static addLog(entry: Omit<PaymentLogEntry, 'id' | 'timestamp'>): void {
    const logEntry: PaymentLogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      ...entry
    };
    
    this.logs.push(logEntry);
    
    // Manter apenas os logs mais recentes
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
    
    // Log no console da UI quando habilitado por ambiente
    if (this.isUiLogEnabled()) {
      const logMethod = entry.level === 'error' ? 'error' : 
                        entry.level === 'warn' ? 'warn' : 'log';
      console[logMethod](`[Payment ${entry.level.toUpperCase()}]`, entry.event, entry.data);
    }
  }

  /**
   * Log genérico de nível INFO
   * Permite chamadas simples como `PaymentLogger.log('Evento', { dados })`.
   * Útil para instrumentação leve sem precisar de métodos específicos.
   */
  static log(event: string, data?: Record<string, unknown>): void {
    this.addLog({
      level: 'info',
      event,
      data: data || {}
    });
  }

  /**
   * Log genérico de nível WARN
   * Fornece semântica clara para avisos operacionais sem tratar como erro.
   * Útil para cenários de fallback, timeout, ou respostas parciais do backend.
   */
  static warn(event: string, data?: Record<string, unknown>): void {
    this.addLog({
      level: 'warn',
      event,
      data: data || {}
    });
  }

  /**
   * Log genérico de nível ERROR
   * Compatível com chamadas existentes `PaymentLogger.error('Evento', erro)`.
   * Converte objetos `Error` para mensagem e pilha, preservando contexto adicional.
   */
  static error(event: string, error: unknown, extra?: Record<string, unknown>): void {
    const asError = error instanceof Error ? error : undefined;
    const data: Record<string, unknown> = {
      message: asError ? asError.message : String(error),
      stack: asError?.stack,
      ...(extra || {})
    };
    this.addLog({ level: 'error', event, data });
  }
  
  /**
   * Log de início de pagamento
   */
  static logPaymentStart(data: {
    reservationId: string;
    amount: number;
    paymentMethod: 'pix' | 'credit_card';
    userId?: string;
    sessionId?: string;
  }): void {
    this.addLog({
      level: 'info',
      event: 'PAYMENT_STARTED',
      data: {
        reservationId: data.reservationId,
        amount: data.amount,
        paymentMethod: data.paymentMethod,
        startTime: new Date().toISOString()
      },
      userId: data.userId,
      sessionId: data.sessionId,
      reservationId: data.reservationId
    });
  }
  
  /**
   * Log de validação pré-pagamento
   */
  static logValidation(data: {
    reservationId: string;
    validationType: string;
    isValid: boolean;
    errors?: string[];
    warnings?: string[];
    testCardDetected?: boolean;
    testCardInfo?: unknown;
  }): void {
    this.addLog({
      level: data.isValid ? 'info' : 'warn',
      event: 'PAYMENT_VALIDATION',
      data: {
        validationType: data.validationType,
        isValid: data.isValid,
        errors: data.errors || [],
        warnings: data.warnings || [],
        testCardDetected: data.testCardDetected || false,
        testCardInfo: data.testCardInfo
      },
      reservationId: data.reservationId
    });
  }
  
  /**
   * Log de tokenização do cartão
   */
  static logTokenization(data: {
    reservationId: string;
    success: boolean;
    cardBrand?: string;
    cardLastFour?: string;
    error?: string;
    processingTime?: number;
  }): void {
    this.addLog({
      level: data.success ? 'info' : 'error',
      event: 'CARD_TOKENIZATION',
      data: {
        success: data.success,
        cardBrand: data.cardBrand,
        cardLastFour: data.cardLastFour,
        error: data.error,
        processingTime: data.processingTime
      },
      reservationId: data.reservationId
    });
  }
  
  /**
   * Log de criação de pagamento
   */
  static logPaymentCreation(data: {
    reservationId: string;
    transactionId?: string;
    success: boolean;
    paymentMethod: 'pix' | 'credit_card';
    amount: number;
    status?: string;
    error?: string;
    processingTime?: number;
  }): void {
    this.addLog({
      level: data.success ? 'info' : 'error',
      event: 'PAYMENT_CREATED',
      data: {
        success: data.success,
        paymentMethod: data.paymentMethod,
        amount: data.amount,
        status: data.status,
        error: data.error,
        processingTime: data.processingTime
      },
      reservationId: data.reservationId,
      transactionId: data.transactionId
    });
  }
  
  /**
   * Log de resposta do backend
   */
  static logBackendResponse(data: {
    reservationId: string;
    endpoint: string;
    statusCode: number;
    success: boolean;
    responseTime: number;
    error?: string;
    responseData?: unknown;
  }): void {
    this.addLog({
      level: data.success ? 'info' : 'error',
      event: 'BACKEND_RESPONSE',
      data: {
        endpoint: data.endpoint,
        statusCode: data.statusCode,
        success: data.success,
        responseTime: data.responseTime,
        error: data.error,
        responseData: data.responseData
      },
      reservationId: data.reservationId
    });
  }
  
  /**
   * Log de erro de pagamento
   */
  static logPaymentError(data: {
    reservationId: string;
    errorType: string;
    errorMessage: string;
    errorCode?: string;
    stackTrace?: string;
    context?: Record<string, unknown>;
  }): void {
    this.addLog({
      level: 'error',
      event: 'PAYMENT_ERROR',
      data: {
        errorType: data.errorType,
        errorMessage: data.errorMessage,
        errorCode: data.errorCode,
        stackTrace: data.stackTrace,
        context: data.context
      },
      reservationId: data.reservationId
    });
  }
  
  /**
   * Log de sucesso de pagamento
   */
  static logPaymentSuccess(data: {
    reservationId: string;
    transactionId: string;
    paymentMethod: 'pix' | 'credit_card';
    amount: number;
    processingTime: number;
    status: string;
  }): void {
    this.addLog({
      level: 'info',
      event: 'PAYMENT_SUCCESS',
      data: {
        paymentMethod: data.paymentMethod,
        amount: data.amount,
        processingTime: data.processingTime,
        status: data.status
      },
      reservationId: data.reservationId,
      transactionId: data.transactionId
    });
  }
  
  /**
   * Log de tentativa de retry
   */
  static logRetryAttempt(data: {
    reservationId: string;
    attemptNumber: number;
    reason: string;
    previousError?: string;
  }): void {
    this.addLog({
      level: 'warn',
      event: 'PAYMENT_RETRY',
      data: {
        attemptNumber: data.attemptNumber,
        reason: data.reason,
        previousError: data.previousError
      },
      reservationId: data.reservationId
    });
  }
  
  /**
   * Obtém logs por reserva
   */
  static getLogsByReservation(reservationId: string): PaymentLogEntry[] {
    return this.logs.filter(log => log.reservationId === reservationId);
  }
  
  /**
   * Obtém logs por nível
   */
  static getLogsByLevel(level: PaymentLogEntry['level']): PaymentLogEntry[] {
    return this.logs.filter(log => log.level === level);
  }
  
  /**
   * Obtém logs recentes
   */
  static getRecentLogs(limit: number = 50): PaymentLogEntry[] {
    return this.logs.slice(-limit);
  }
  
  /**
   * Calcula métricas de pagamento
   */
  static getPaymentMetrics(): PaymentMetrics {
    // `paymentLogs` seria usado futuramente para análises detalhadas; removido para evitar warning de unused
    
    const totalAttempts = this.logs.filter(log => log.event === 'PAYMENT_STARTED').length;
    const successfulPayments = this.logs.filter(log => log.event === 'PAYMENT_SUCCESS').length;
    const failedPayments = this.logs.filter(log => log.event === 'PAYMENT_ERROR').length;
    
    // Calcular tempo médio de processamento
    const successLogs = this.logs.filter(log => 
      log.event === 'PAYMENT_SUCCESS' && log.data.processingTime
    );
    const averageProcessingTime = successLogs.length > 0 
      ? successLogs.reduce((sum, log) => sum + Number(log.data.processingTime || 0), 0) / successLogs.length
      : 0;
    
    // Contar erros por tipo
    const errorsByType: Record<string, number> = {};
    this.logs.filter(log => log.event === 'PAYMENT_ERROR').forEach(log => {
      const errorType = String((log.data as Record<string, unknown>).errorType || 'UNKNOWN');
      errorsByType[errorType] = (errorsByType[errorType] || 0) + 1;
    });
    
    // Contar uso de cartões de teste
    const testCardUsage: Record<string, number> = {};
    this.logs.filter(log => 
      log.event === 'PAYMENT_VALIDATION' && log.data.testCardDetected
    ).forEach(log => {
      const cardName = String(((log.data as Record<string, unknown>).testCardInfo as Record<string, unknown> | undefined)?.name || 'UNKNOWN_TEST_CARD');
      testCardUsage[cardName] = (testCardUsage[cardName] || 0) + 1;
    });
    
    return {
      totalAttempts,
      successfulPayments,
      failedPayments,
      averageProcessingTime,
      errorsByType,
      testCardUsage
    };
  }
  
  /**
   * Exporta logs para análise
   */
  static exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
  
  /**
   * Limpa logs antigos
   */
  static clearOldLogs(olderThanHours: number = 24): void {
    const cutoffTime = new Date(Date.now() - olderThanHours * 60 * 60 * 1000);
    this.logs = this.logs.filter(log => log.timestamp > cutoffTime);
  }
}
